import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpMethod, HttpPlus } from 'src/app/SayTS/http-plus';
import { SharedService } from 'src/app/_shared/shared.service';
import { IReservation, IDurationOption, IVehicleType } from '../models/reservation';
import { IStation } from '../models/station';
import { ILocationSettings } from '../models/location-settings';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class RepService {

  private baseUrl = '';

  constructor(
    private http: HttpPlus,
    private httpClient: HttpClient,
    private shared: SharedService
  ) { }

  /** Invalidate cached static lookups (call after save of stations/locations). */
  resetLookupCache(): void {
    this.shared.resetCached(this.baseUrl + 'locations');
    this.shared.resetCached(this.baseUrl + 'duration-options');
    this.shared.resetCached(this.baseUrl + 'vehicle-types');
    this.shared.resetCached(this.baseUrl + 'locations/', true);
  }

  // ==================== AUTH ====================

  async register(data: {
    username: string;
    email: string;
    password: string;
    fullname: string;
    company_name: string;
    ico?: string;
  }): Promise<IRegisterResult> {
    // Použijeme přímo HttpClient aby se chyby nepohltily v HttpPlus.logError
    return this.httpClient.post<IRegisterResult>(
      environment.apiUrl + this.baseUrl + 'register',
      data
    ).toPromise();
  }

  async verifyEmail(token: string): Promise<any> {
    return this.http.get(this.baseUrl + 'verify-email/' + token);
  }

  async resendVerification(email: string): Promise<any> {
    return this.http.post(this.baseUrl + 'resend-verification', { email });
  }

  // ==================== RESERVATIONS ====================

  async getReservations(params: any): Promise<{ count: number; rows: IReservation[] }> {
    return this.http.post(this.baseUrl + 'reservations/list', params) as Promise<any>;
  }

  async getReservation(id: number): Promise<IReservation> {
    return this.http.get(this.baseUrl + 'reservations/get/' + id) as Promise<any>;
  }

  async saveReservation(reservation: Partial<IReservation>): Promise<IReservation> {
    return this.http.post(this.baseUrl + 'reservations/save', reservation) as Promise<any>;
  }

  async cancelReservation(id: number, opts?: { notify_user?: boolean; reason?: string }): Promise<IReservation> {
    return this.http.post(this.baseUrl + 'reservations/cancel/' + id, opts || {}) as Promise<any>;
  }

  async checkAvailability(params: {
    id_location: number;
    id_station: number;
    date_from: string;
    duration_minutes: number;
    id_reservation?: number;
  }): Promise<{ available: boolean; date_from: string; date_to: string; duration_minutes: number }> {
    return this.http.post(this.baseUrl + 'reservations/check-availability', params) as Promise<any>;
  }

  // ==================== LOCATIONS ====================

  async getLocations(): Promise<any[]> {
    return this.shared.getCached(this.http, this.baseUrl + 'locations', HttpMethod.GET);
  }

  async getStationsForLocation(idLocation: number): Promise<IStation[]> {
    return this.shared.getCached(this.http, this.baseUrl + 'locations/' + idLocation + '/stations', HttpMethod.GET);
  }

  async getLocationAvailability(idLocation: number, date?: string): Promise<{
    date: string;
    opening_time: string;
    closing_time: string;
    stations: any[];
  }> {
    const params = date ? { date } : {};
    return this.http.get(this.baseUrl + 'locations/' + idLocation + '/availability', params) as Promise<any>;
  }

  // ==================== OPTIONS ====================

  async getDurationOptions(): Promise<IDurationOption[]> {
    return this.shared.getCached(this.http, this.baseUrl + 'duration-options', HttpMethod.GET);
  }

  async getVehicleTypes(): Promise<IVehicleType[]> {
    return this.shared.getCached(this.http, this.baseUrl + 'vehicle-types', HttpMethod.GET);
  }

  // ==================== ADMIN - VEHICLE TYPES ====================

  async listVehicleTypes(params: any = {}): Promise<{ count: number; rows: IVehicleType[] }> {
    return this.http.post(this.baseUrl + 'vehicle-types-admin/list', params) as Promise<any>;
  }

  async getVehicleType(id: number): Promise<IVehicleType> {
    return this.http.get(this.baseUrl + 'vehicle-types-admin/get/' + id) as Promise<any>;
  }

  async saveVehicleType(vt: Partial<IVehicleType> & { is_active?: boolean | number; sort_order?: number }): Promise<IVehicleType> {
    const res = await this.http.post(this.baseUrl + 'vehicle-types-admin/save', vt) as IVehicleType;
    this.shared.resetCached(this.baseUrl + 'vehicle-types');
    return res;
  }

  async deleteVehicleType(id: number): Promise<any> {
    const res = await this.http.delete(this.baseUrl + 'vehicle-types-admin/delete/' + id);
    this.shared.resetCached(this.baseUrl + 'vehicle-types');
    return res;
  }

  // ==================== ADMIN - STATIONS ====================

  async getStations(params: any): Promise<{ count: number; rows: IStation[] }> {
    return this.http.post(this.baseUrl + 'stations/list', params) as Promise<any>;
  }

  async getAllStations(): Promise<IStation[]> {
    return this.http.get(this.baseUrl + 'stations/all') as Promise<any>;
  }

  async getStation(id: number): Promise<IStation> {
    return this.http.get(this.baseUrl + 'stations/get/' + id) as Promise<any>;
  }

  async saveStation(station: Partial<IStation>): Promise<IStation> {
    const res = await this.http.post(this.baseUrl + 'stations/save', station) as IStation;
    this.resetLookupCache();
    return res;
  }

  async deleteStation(id: number): Promise<any> {
    const res = await this.http.delete(this.baseUrl + 'stations/delete/' + id);
    this.resetLookupCache();
    return res;
  }

  // ==================== ADMIN - LOCATION SETTINGS ====================

  async getLocationSettings(idLocation: number): Promise<ILocationSettings> {
    return this.http.get(this.baseUrl + 'location-settings/get/' + idLocation) as Promise<any>;
  }

  async saveLocationSettings(settings: Partial<ILocationSettings>): Promise<ILocationSettings> {
    return this.http.post(this.baseUrl + 'location-settings/save', settings) as Promise<any>;
  }

  async regenerateApiKey(idLocation: number): Promise<{ api_key: string }> {
    return this.http.post(this.baseUrl + 'location-settings/regenerate-api-key/' + idLocation, {}) as Promise<any>;
  }

  async listLocationSettings(params?: { is_rep_enabled?: boolean }): Promise<ILocationSettings[]> {
    return this.http.post(this.baseUrl + 'location-settings/list', params || {}) as Promise<any>;
  }

  // ==================== ADMIN - USERS ====================

  async adminGetUsers(params: any): Promise<{ count: number; rows: any[] }> {
    return this.http.post(this.baseUrl + 'admin/users/list', params) as Promise<any>;
  }

  async adminGetUser(id: number): Promise<any> {
    return this.http.get(this.baseUrl + 'admin/users/get/' + id) as Promise<any>;
  }

  async adminSaveUser(user: any): Promise<any> {
    return this.http.post(this.baseUrl + 'admin/users/save', user) as Promise<any>;
  }

  async adminDeleteUser(id: number): Promise<any> {
    return this.http.delete(this.baseUrl + 'admin/users/delete/' + id);
  }

  // ==================== NOTIFICATIONS ====================

  async getNotifications(): Promise<{ count: number; rows: INotification[] }> {
    return this.http.get(this.baseUrl + 'notifications') as Promise<any>;
  }

  async markNotificationRead(id: number): Promise<any> {
    return this.http.post(this.baseUrl + 'notifications/read/' + id, {});
  }

  async markAllNotificationsRead(): Promise<any> {
    return this.http.post(this.baseUrl + 'notifications/read-all', {});
  }
}

export interface INotification {
  id_notification: number;
  id_user: number;
  id_reservation?: number;
  type: 'UPDATED' | 'CANCELLED' | 'CONFIRMED';
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

export interface IRegisterResult {
  success: boolean;
  message: string;
}

/**
 * Helper pro parsování validačních chyb z BE (422)
 */
export function parseValidationErrors(error: any): string {
  if (!error?.error) {
    return 'Neznámá chyba';
  }

  const errors = error.error;

  // Mapování validačních klíčů na české texty
  const messages: { [key: string]: { [rule: string]: string } } = {
    email: {
      'validation.unique': 'Tento email je již registrován',
      'validation.email': 'Neplatný formát emailu',
      'validation.required': 'Email je povinný'
    },
    username: {
      'validation.unique': 'Toto uživatelské jméno je již obsazeno',
      'validation.required': 'Uživatelské jméno je povinné',
      'validation.min': 'Uživatelské jméno je příliš krátké'
    },
    password: {
      'validation.required': 'Heslo je povinné',
      'validation.min': 'Heslo musí mít alespoň 6 znaků'
    },
    fullname: {
      'validation.required': 'Jméno je povinné',
      'validation.min': 'Jméno je příliš krátké'
    },
    company_name: {
      'validation.required': 'Název společnosti je povinný',
      'validation.min': 'Název společnosti je příliš krátký'
    },
    ico: {
      'validation.max': 'IČO může mít maximálně 20 znaků'
    }
  };

  const result: string[] = [];

  for (const field of Object.keys(errors)) {
    const fieldErrors = errors[field];
    if (Array.isArray(fieldErrors)) {
      for (const err of fieldErrors) {
        const translated = messages[field]?.[err] || err;
        result.push(translated);
      }
    }
  }

  return result.length > 0 ? result.join('. ') : 'Chyba validace';
}
